import {
  useCallback,
  useRef,
  useEffect,
  useState,
  useMemo,
  useReducer
} from "react"
import {
  ModalDialog,
  SecondaryNav,
  MultiLevelDropdown,
  Typography,
  Button,
  Spinner
} from "src/blitz"
import { useSelector, useDispatch } from "react-redux"
import { t } from "src/translations/help"
import { useParams, useLocation } from "react-router-dom"
import { API } from "src/shared/lib/amplify"
import DateRangePicker from "src/blitz/DateRange/DateRangePicker"
import {
  getDevicesByBuilding,
  getEquipmentTypesByBuilding,
  getEquipmentsByBuilding
} from "./graphql"
import {
  ADD,
  CUSTOM_RANGE,
  DATE,
  END,
  FOUR_WEEKS,
  MONTH,
  NONE,
  ONE_DAY,
  ONE_MONTH,
  ONE_WEEK,
  START,
  SUBTRACT
} from "src/blitz/DateRange/constant"
import {
  displayDateFormatter,
  getCalculatedDay
} from "src/blitz/DateRange/helper"
import { RawDataReport } from "./RawDataReport"
import { ExceptionReport } from "./ExceptionReport"
import { ChillerReport } from "./ChillerReport"
import AHUReport from "./AHUReport/AHUPerformanceReport"
import { AlarmsAndEventsReport } from "./AlarmsAndEventsReport"
import { DigitalInspectionReport } from "./DigitalInspectionReport"
import { TrsReport } from "./TrsReport"
import {
  selectBuildingSummary,
  selectEquipmentTypes,
  selectExplicitEquipmentTypes
} from "src/redux/slicers/buildingSummarySlice"
import {
  setReportStatus,
  getReportStatus,
  setReportDialog,
  selectReportDialog,
  getIsValid,
  getIsDataExceeded,
  setFilter,
  getFilter,
  setRawDataEquipments,
  getReportType,
  setReportType,
  getBuildingList,
  setMultiBuildingsDialog,
  selectAppliedBuildings,
  setBuildingEquipmentTypes,
  getEquipments,
  setSelectedEquipments,
  setSelectedProperties
} from "src/redux/slicers/reportSlice"
import {
  frameEquipmentsTree,
  transformCustomProperties,
  getApplicablePropertiesFromCharacteristics
} from "./helper"
import { filterEquipmentByDeviceIds } from "src/utils/analyticsHelper"
import {
  RAW_DATA,
  CHILLER_REPORT,
  TRS_REPORT,
  EXCEPTION_REPORT,
  SELECT_ALL,
  LANG_ENGLISH,
  CHILLER,
  ALARMS_AND_EVENTS_REPORT,
  reportTimePeriodOptions,
  defaultTimePeriodOptions,
  systemOfMeasureOptions,
  languageOptions,
  DIGITAL_INSPECTION_REPORT_VRF,
  BOILER,
  HP_CHILLER,
  getPreSelectedEquipType,
  EMPTY_EQUIPMENT_TYPE,
  AHU_REPORT,
  AHU
} from "./constants"
import style from "./ReportDialog.module.scss"
import LoadingSpinner from "src/blitz/Loading/Loading"
import { ACTIONS } from "src/constants"
import { useQueryDateValues } from "src/hooks"
import { useQuery } from "src/hooks/APIHooks"
import { useActiveDeviceIds } from "src/hooks/useActiveDeviceIds"
import { ReportErrorMessage } from "src/components/shared"
import moment from "moment"
import { decideShowVRFDIReport } from "src/utils/OfferingUtils/offeringUtils"
import { getPropertyMappingByEquipmentID } from "src/components/equipmentSummary/graphql"
import { cloneDeep, get } from "lodash"
import {
  selectSelectedBuildingsEquipmentTypeList,
  selectUserAccess
} from "src/redux/slicers/homeSlice"
import {
  selectchartsFilter,
  selectAllChartsData
} from "src/redux/slicers/performanceSlice"
import { selectEquipmentSummary } from "src/redux/slicers/equipmentSummarySlice"
import { trackEvent } from "src/amplitude.js"
import { USER_EVENTS } from "src/amplitude-categories"
import momentTimeZone from "moment-timezone"
import translate, { TranslateComponent } from "src/common/translations"
import { subDays } from "date-fns"
import SaveDialog from "../SaveDialog"
import { getDateFormatByLocale } from "src/utils/CommonMethods"
import store from "src/redux/Store"
import { decodeURLSearchParams } from "../Charts/Helper/common"
import { accessControlFunc } from "src/components/accessControl"
import { AIR_HANDLER } from "src/components/buildingSummary/constants"

const ReportDialog = () => {
  const { data: buildingSummary } = useSelector(selectBuildingSummary)
  const hasChillerEquipment = useSelector((state: any) =>
    selectExplicitEquipmentTypes(state, "Chiller")
  )
  const hasAhuEquipment = useSelector((state: any) =>
    selectExplicitEquipmentTypes(state, "AHU")
  )
  const params = useParams()
  const { buildingId } = buildingSummary?.buildingId ? buildingSummary : params

  const { activeDeviceIdsRef, devicesFetched, fetchBuildingEquipment } =
    useActiveDeviceIds(buildingId)
  const rawEquipmentDataRef = useRef<any[]>(null)
  const ehrParsedDataRef = useRef<{
    parsedData: any
    allEquipment: any[]
  } | null>(null)
  const chillerEquipmentDataRef = useRef<any[] | null>(null)
  const ahuEquipmentDataRef = useRef<any[] | null>(null)

  const [openCalendar, setOpenCalendar] = useState(false)
  const [noAvailableEquipmentTypes, setAvailableEquipmentTypes] =
    useState(false)
  const [equipmentTypesLoading, setEquipmentTypesLoading] = useState(false)
  const { loading, completed, error } = useSelector(getReportStatus)
  const appliedBuildings = useSelector(selectAppliedBuildings)
  const userAccess = useSelector(selectUserAccess)
  const isValid = useSelector(getIsValid)
  const { loading: buildingListLoading } = useSelector(getBuildingList)
  const isDataExceeded = useSelector(getIsDataExceeded)
  const { queryStartDate, queryEndDate } = useQueryDateValues()

  const startDates = decodeURLSearchParams(queryStartDate)?.[0]
  const endDates = decodeURLSearchParams(queryEndDate)?.[0]

  const createSafeDate = (
    dateString: string | undefined,
    fallbackDate: Date
  ) => {
    if (!dateString) return fallbackDate
    const date = new Date(dateString)
    return isNaN(date.getTime()) ? fallbackDate : date
  }

  const EquipmentTypesMapping = useSelector(selectEquipmentTypes)
  const equipments = useSelector(getEquipments)
  const selectedBuildingsEquipmentTypeList = useSelector(
    selectSelectedBuildingsEquipmentTypeList
  )
  const selectedLang = store?.getState()?.userData?.selectedLanguage ?? "en"
  const route = useLocation()
  const { data: equipment } = useSelector(selectEquipmentSummary)
  const equipmentId = params?.equipmentId || equipment?.id
  const chartsFilter = useSelector(selectchartsFilter)
  const { chartsProps } = useSelector(selectAllChartsData)

  // Get the first chart's filter for equipment properties
  const chartFilter = useMemo(() => {
    const chartId = Object.keys(chartsFilter).find(
      (key: any) => chartsFilter[key].isFirst
    )
    return chartId ? chartsFilter[chartId] : null
  }, [chartsFilter])

  // Determine if we're at building level (not equipment level)
  const isBuilding = useMemo(() => {
    return buildingId && !equipmentId
  }, [buildingId, equipmentId])

  const completedMessage = completed ? "Completed" : ""

  // RDR and DI Report
  const processEquipmentList = async (data: any = []) => {
    dispatch(setRawDataEquipments({ type: "Loading" }))
    // activeDeviceIdsRef.current is null until the device list has been fetched
    // (updated synchronously in useMemo, so no render-lag). When null, skip
    // filtering so equipment types appear while devices are still loading;
    // the useEffect on devicesFetched will re-run once devices are ready.
    const deviceIds = activeDeviceIdsRef.current
    const filteredData =
      deviceIds !== null ? filterEquipmentByDeviceIds(data, deviceIds) : data
    setAvailableEquipmentTypes(!(filteredData?.length > 0))
    const equipmentTypes = frameEquipmentsTree(filteredData, false)
    const newEquipmentTypes = await addMissingCharacteristics(equipmentTypes)
    setEquipmentTypes(newEquipmentTypes)
    if (buildingId && selectedEquipmentType?.value) {
      const selectedEquipmentTypeObject = newEquipmentTypes?.filter(
        ({ value }) => value === selectedEquipmentType?.value
      )?.[0]
      if (selectedEquipmentTypeObject) {
        if (
          !selectedEquipmentTypeObject?.sub &&
          selectedEquipmentTypeObject?.options
        ) {
          selectedEquipmentTypeObject?.options?.forEach((optionObj) => {
            if (
              optionObj?.sub?.find((subObj) => subObj?.id === equipment?.id)
            ) {
              dispatch(
                setRawDataEquipments({
                  type: "Success",
                  data: optionObj?.sub || []
                })
              )
              dispatch(
                setFilter({
                  equipmentType: {
                    label: optionObj?.title,
                    value: optionObj?.value
                  },
                  error: false,
                  isCompleted: false
                })
              )
            }
          })
        } else {
          dispatch(
            setRawDataEquipments({
              type: "Success",
              data: selectedEquipmentTypeObject?.sub || []
            })
          )
        }
      } else {
        dispatch(setRawDataEquipments({ type: "Failure" }))
      }
    }
    dispatch(setBuildingEquipmentTypes(newEquipmentTypes))
  }

  const processEHREquipmentTypes = (parsedData: any, allEquipment: any[]) => {
    const deviceIds = activeDeviceIdsRef.current
    const filteredEquipment =
      deviceIds !== null
        ? filterEquipmentByDeviceIds(allEquipment, deviceIds)
        : allEquipment

    const activeTypes = new Set(filteredEquipment.map((eq: any) => eq.type))

    let equipmentTypes = []
    Object.keys(parsedData)?.forEach((item) => {
      if (item !== NONE && activeTypes.has(parsedData[item])) {
        equipmentTypes = [
          ...equipmentTypes,
          { title: item, value: parsedData[item], type: "button" }
        ]
      }
    })
    setEquipmentTypes(
      equipmentTypes.length > 0
        ? [...SELECT_ALL, ...equipmentTypes]
        : equipmentTypes
    )
    dispatch(setBuildingEquipmentTypes(equipmentTypes))
  }

  const fetchEquipmentPropertiesByEquipmentID = async (equipmentId) => {
    try {
      const response = await API.graphql({
        query: getPropertyMappingByEquipmentID,
        variables: { id: equipmentId },
        authMode: "userPool"
      })
      const data = get(
        response,
        "data.getPropertyMappingByEquipmentID.items",
        null
      )
      return data
    } catch (err) {
      throw err
    }
  }
  const addMissingCharacteristics = async (equipTypes) => {
    const types = [CHILLER, BOILER, HP_CHILLER]
    const equipObj: any = {}
    const equipWithMissing: any = []
    for (let i = 0; i < types.length; i++) {
      const type = types?.[i]

      if (!equipObj?.[type]) {
        equipObj[type] = {}
      }
      const typeObj = equipTypes?.find((i) => i?.value === type)
      const equips = typeObj?.sub || []
      if (equips?.length > 0) {
        equips?.forEach((eO: any) => {
          const { id } = eO
          equipWithMissing?.push(eO)
          equipObj[type][id] = eO
        })
      }
    }

    if (equipWithMissing?.length > 0) {
      const fetchallEquipmentMissingProperties = async () => {
        try {
          const analyticsPromises = equipWithMissing?.map(async (eO) => {
            const responseData = await fetchEquipmentPropertiesByEquipmentID(
              eO?.id
            )
            if (responseData) {
              const response =
                getApplicablePropertiesFromCharacteristics(
                  responseData,
                  eO?.type
                ) || responseData
              if (eO?.type === CHILLER) {
                const { instance, compCondType } = transformCustomProperties(
                  response || []
                )
                if (compCondType) {
                  equipObj[eO?.type][eO?.id].compCondType = compCondType
                }
                if (instance) {
                  equipObj[eO?.type][eO?.id].instance = instance
                }
              } else if (eO?.type === BOILER) {
                const compCondType = (
                  (response || [])?.find(
                    (c) => c?.targetKey === "ApplicationType"
                  )?.value || ""
                )
                  ?.trim()
                  ?.replace(/\s/g, "")
                equipObj[eO?.type][eO?.id].compCondType = compCondType
              } else if (eO?.type === HP_CHILLER) {
                const { instance } = transformCustomProperties(response || [])
                const compCondType = (
                  (response || [])?.find(
                    (c) => c?.targetKey === "DistributionSystemType"
                  )?.value || ""
                )
                  ?.trim()
                  ?.replace(/\s/g, "")
                equipObj[eO?.type][eO?.id].compCondType = compCondType
                equipObj[eO?.type][eO?.id].instance = instance
              }
            }
          })
          await Promise.all(analyticsPromises)
        } catch (err) {
          console.warn("Error in adding missing properties to equipments")
        }
      }
      await fetchallEquipmentMissingProperties()
      for (let i = 0; i < types.length; i++) {
        const subArray = Object?.values(equipObj?.[types?.[i]] || {})
        const index = equipTypes?.findIndex((o) => o?.value === types?.[i])
        if (index !== -1) {
          equipTypes[index].sub = subArray
        }
      }
    }
    return equipTypes
  }

  const { refetch: refetchEquipmentsByEquipmentId } = useQuery({
    query: getEquipmentsByBuilding,
    dataPath: "data.listEquipmentsByBuildingAndActiveAndType.items",
    disableInitialLoad: true,
    onSuccess: (data: any) => {
      dispatch(
        setRawDataEquipments({
          type: "Success",
          data: data || []
        })
      )
    },
    onError: () => {
      dispatch(setRawDataEquipments({ type: "Failure" }))
    }
  })
  const { refetch: refetchDevicesByBuildingId } = useQuery({
    query: getDevicesByBuilding,
    dataPath: "data.listDevicesByBuilding.items",
    disableInitialLoad: true,
    onSuccess: (data: any) => {
      const responseData = data?.map((item: any) => {
        if (!item.name) {
          item.name = item?.serial
        }
        return item
      })
      dispatch(
        setRawDataEquipments({
          type: "Success",
          data: responseData || []
        })
      )
    },
    onError: () => {
      dispatch(setRawDataEquipments({ type: "Failure" }))
    }
  })

  const {
    equipmentType: selectedEquipmentType,
    timePeriod,
    startDate,
    endDate,
    language,
    systemOfMeasure
  } = useSelector(getFilter)
  const [selectedDate, setSelectedDate] = useState({
    start: new Date(startDate),
    end: new Date(endDate)
  })
  const [calendarView, setCalendarView] = useState({
    start: DATE,
    end: DATE
  })

  const getLanguageDetails = (langOptions, language) => {
    const selectedLanguageOption = langOptions?.find(
      (obj) => obj?.value === language
    )
    if (selectedLanguageOption?.title) {
      return { title: selectedLanguageOption?.title, langCode: language }
    }
    dispatch(
      setFilter({
        language: LANG_ENGLISH
      })
    )
    return {
      title: langOptions?.find((obj) => obj?.value === LANG_ENGLISH).title,
      langCode: language
    }
  }

  const languageTitleSelected = useMemo(() => {
    return getLanguageDetails(languageOptions, language)
  }, [language])

  const systemOfMeasureSelected = useMemo(
    () => ({
      title: systemOfMeasureOptions?.find(
        (obj) => obj?.value === systemOfMeasure
      )?.title,
      metricCode: systemOfMeasure
    }),
    [systemOfMeasure]
  )

  const [selectedLanguage, setSelectedLanguage] = useState(
    languageTitleSelected
  )
  const [selectedMetric, setSelectedMetric] = useState(systemOfMeasureSelected)
  const [
    buildingLevelEquipmentProperties,
    setBuildingLevelEquipmentProperties
  ] = useState(null)

  const DIReportDisabled = useMemo(() => {
    let availableEquipmentTypes = []
    if (route.pathname === "/buildings") {
      availableEquipmentTypes = selectedBuildingsEquipmentTypeList
    } else {
      availableEquipmentTypes = Object.keys(EquipmentTypesMapping).filter(
        (equipmentType) => EquipmentTypesMapping[equipmentType]
      )
    }
    return !decideShowVRFDIReport(
      buildingSummary?.offeringCodes,
      availableEquipmentTypes
    )
  }, [
    buildingSummary,
    EquipmentTypesMapping,
    equipments,
    selectedBuildingsEquipmentTypeList,
    route.pathname
  ])

  const rawDataCompRef = useRef(null)
  const exceptionCompRef = useRef(null)
  const chillerCompRef = useRef(null)
  const ahuCompRef = useRef(null)
  const trsCompRef = useRef(null)
  const alarmAndEventCompRef = useRef(null)
  const digitalInspCompRef = useRef(null)
  const dispatch = useDispatch()
  const showReportDialog = useSelector(selectReportDialog)
  const reportType = useSelector(getReportType)
  const [equipmentTypes, setEquipmentTypes] = useState<any>([])
  const cprEligible = useMemo(() => {
    return isBuilding
      ? hasChillerEquipment
      : equipment?.type === CHILLER || equipment?.type === HP_CHILLER
  }, [isBuilding, hasChillerEquipment, equipment?.type])
  const ahuEligible = useMemo(() => {
    return isBuilding ? hasAhuEquipment : equipment?.type === AHU
  }, [isBuilding, hasAhuEquipment, equipment?.type])
  const reportTypes: any = useMemo(() => {
    const options = [
      {
        title: "AHU Performance",
        disabled:
          !accessControlFunc({
            id: "cc.reports.ahu-performance",
            userAccess
          }) ||
          !ahuEligible ||
          !buildingSummary?.enableReports?.AHUPR,
        type: "button",
        value: AHU_REPORT,
        id: AHU_REPORT,
        testId: "reports-dropdown-select-ahuReport"
      },
      {
        title: "Alarms and Events",
        type: "button",
        value: ALARMS_AND_EVENTS_REPORT,
        id: ALARMS_AND_EVENTS_REPORT,
        testId: "reports-dropdown-select-alarmsandevents"
      },
      {
        title: "Chiller Performance",
        disabled: !cprEligible || !buildingSummary?.enableReports?.CPR,
        type: "button",
        value: CHILLER_REPORT,
        id: CHILLER_REPORT,
        testId: "reports-dropdown-select-chillerReport"
      },
      {
        title: "Exception History",
        type: "button",
        disabled: !buildingSummary?.enableReports?.EHR,
        value: EXCEPTION_REPORT,
        id: EXCEPTION_REPORT,
        testId: "reports-dropdown-select-exceptionRreport"
      },
      {
        title: "Raw Data",
        type: "button",
        value: RAW_DATA,
        id: RAW_DATA,
        testId: "reports-dropdown-select-rawData"
      },
      {
        title: "VRF Digital Inspection Report",
        type: "button",
        value: DIGITAL_INSPECTION_REPORT_VRF,
        id: DIGITAL_INSPECTION_REPORT_VRF,
        disabled: DIReportDisabled,
        testId: "reports-dropdown-select-digitalInspectionReport"
      },
      {
        title: "TRS Report",
        type: "button",
        value: TRS_REPORT,
        id: TRS_REPORT,
        testId: "reports-dropdown-select-trsReport"
      }
    ]

    return options
  }, [userAccess, cprEligible, buildingSummary, DIReportDisabled])

  useEffect(() => {
    dispatch(setReportStatus(ACTIONS.RESET))
    if (buildingId) fetchEquipments()
  }, [buildingId, reportTypes, reportType, appliedBuildings])

  useEffect(() => {
    const isRdrOrDI =
      reportType?.value === RAW_DATA ||
      reportType?.value === DIGITAL_INSPECTION_REPORT_VRF
    if (devicesFetched && isRdrOrDI && rawEquipmentDataRef.current) {
      processEquipmentList(rawEquipmentDataRef.current)
    } else if (
      devicesFetched &&
      reportType?.value === EXCEPTION_REPORT &&
      ehrParsedDataRef.current
    ) {
      const { parsedData, allEquipment } = ehrParsedDataRef.current
      processEHREquipmentTypes(parsedData, allEquipment)
    } else if (
      devicesFetched &&
      reportType?.value === CHILLER_REPORT &&
      chillerEquipmentDataRef.current
    ) {
      const activeTypes = setChillerReportEquipmentTypes(
        chillerEquipmentDataRef.current
      )
      const currentVal = selectedEquipmentType?.value
      if (currentVal && !activeTypes.some((t) => t.value === currentVal)) {
        dispatch(setFilter({ equipmentType: EMPTY_EQUIPMENT_TYPE }))
      }
    } else if (
      devicesFetched &&
      reportType?.value === AHU_REPORT &&
      ahuEquipmentDataRef.current
    ) {
      const activeTypes = setAHUReportEquipmentTypes(
        ahuEquipmentDataRef.current
      )
      const currentVal = selectedEquipmentType?.value
      if (currentVal && !activeTypes.some((t) => t.value === currentVal)) {
        dispatch(setFilter({ equipmentType: EMPTY_EQUIPMENT_TYPE }))
      }
    }
  }, [devicesFetched])

  useEffect(() => {
    if (
      reportType?.value === CHILLER_REPORT ||
      reportType?.value === AHU_REPORT
    ) {
      const qDate =
        endDates && endDates !== "undefined" ? new Date(endDates) : new Date()
      const maxStartDate = moment(qDate).startOf("month").toDate()
      const monthEndDate = moment(qDate).endOf("month").toDate()
      const maxEndDate = buildingSummary?.tz
        ? momentTimeZone().tz(buildingSummary?.tz)?.toDate()
        : momentTimeZone().tz("UTC")?.toDate()
      setSelectedDate({
        start: maxStartDate,
        end: moment(monthEndDate)?.isAfter(maxEndDate)
          ? maxEndDate
          : monthEndDate
      })
      dispatch(
        setFilter({
          timePeriod: ONE_MONTH,
          startDate: maxStartDate.toISOString(),
          endDate: moment(monthEndDate)?.isAfter(maxEndDate)
            ? maxEndDate?.toISOString()
            : monthEndDate?.toISOString()
        })
      )
      setCalendarView({
        start: MONTH,
        end: DATE
      })
    } else if (reportType?.value === TRS_REPORT) {
      const qDate =
        endDates && endDates !== "undefined" ? new Date(endDates) : new Date()
      const maxStartDate = moment(qDate).startOf("month").toDate()
      const monthEndDate = moment(qDate).endOf("month").toDate()
      const maxEndDate = buildingSummary?.tz
        ? momentTimeZone().tz(buildingSummary?.tz)?.toDate()
        : momentTimeZone().tz("UTC")?.toDate()
      setSelectedDate({
        start: maxStartDate,
        end: moment(monthEndDate)?.isAfter(maxEndDate)
          ? maxEndDate
          : monthEndDate
      })
      dispatch(
        setFilter({
          timePeriod: ONE_MONTH,
          startDate: maxStartDate.toISOString(),
          endDate: moment(monthEndDate)?.isAfter(maxEndDate)
            ? maxEndDate?.toISOString()
            : monthEndDate?.toISOString()
        })
      )
      setCalendarView({
        start: DATE,
        end: DATE
      })
    } else {
      // Helper function to validate and create date
      const createValidDate = (dateValue: any, fallbackDate: Date) => {
        if (!dateValue) return fallbackDate
        const date = new Date(dateValue)
        return isNaN(date.getTime()) ? fallbackDate : date
      }

      const defaultStartDate = subDays(new Date(), 1)
      const defaultEndDate = new Date()

      const validStartDate = createValidDate(startDates, defaultStartDate)
      const validEndDate = createValidDate(endDates, defaultEndDate)

      setSelectedDate({
        start: validStartDate,
        end: validEndDate
      })
      dispatch(
        setFilter({
          startDate: validStartDate.toISOString(),
          endDate: validEndDate.toISOString()
        })
      )
    }
  }, [])

  // Fetch equipment properties for building-level Raw Data Report
  useEffect(() => {
    const isBuildingLevelByPath =
      route.pathname.includes("/building/") &&
      (route.pathname.includes("/summary") ||
        route.pathname.includes("/reports") ||
        route.pathname.includes("/advisories")) &&
      !route.pathname.includes("/equipment/")
    const isEquipmentSummaryByPath =
      route.pathname.includes("/building/") &&
      route.pathname.includes("/equipment/") &&
      (route.pathname.includes("/summary") ||
        route.pathname.includes("/reports") ||
        route.pathname.includes("/advisories"))
    const routeChecking =
      (isBuilding && isBuildingLevelByPath) || isEquipmentSummaryByPath
    const isRawDataReport = reportType?.value === RAW_DATA
    const hasEquipments = equipments && equipments.length > 0
    const noChartFilter = !chartFilter

    if (routeChecking && isRawDataReport && hasEquipments && noChartFilter) {
      // Fetch properties for the first equipment in the list
      const firstEquipmentId = equipments[0]?.id
      if (firstEquipmentId) {
        fetchEquipmentPropertiesByEquipmentID(firstEquipmentId)
          .then((properties) => {
            setBuildingLevelEquipmentProperties(properties)
          })
          .catch((error) => {
            console.error("Failed to fetch equipment properties:", error)
            setBuildingLevelEquipmentProperties(null)
          })
      }
    } else if (!routeChecking || !isRawDataReport) {
      // Reset when not at building level or not raw data report
      setBuildingLevelEquipmentProperties(null)
    }
  }, [equipments, chartFilter, reportType, route.pathname, isBuilding])

  const setChillerReportEquipmentTypes = (allEquipment: any[]) => {
    const hpChillerPermission =
      accessControlFunc({
        id: "cc.hp-chiller.features",
        userAccess
      }) || false

    const deviceIds = activeDeviceIdsRef.current
    const filteredEquipment =
      deviceIds !== null
        ? filterEquipmentByDeviceIds(allEquipment, deviceIds)
        : allEquipment
    const activeTypes = new Set(filteredEquipment.map((eq: any) => eq.type))

    const types = []
    if (activeTypes.has(CHILLER)) {
      types.push({ title: "Chiller", value: CHILLER, type: "button", sub: [] })
    }
    if (hpChillerPermission && activeTypes.has(HP_CHILLER)) {
      types.push({
        title: "Heat Pump Chiller",
        value: HP_CHILLER,
        type: "button",
        sub: []
      })
    }
    setEquipmentTypes(types)
    return types
  }
  const setAHUReportEquipmentTypes = (allEquipment: any[]) => {
    const deviceIds = activeDeviceIdsRef.current
    const filteredEquipment =
      deviceIds !== null
        ? filterEquipmentByDeviceIds(allEquipment, deviceIds)
        : allEquipment
    const activeTypes = new Set(filteredEquipment.map((eq: any) => eq.type))

    const types = []
    if (activeTypes.has(AHU)) {
      types.push({
        title: AIR_HANDLER,
        value: AHU,
        type: "button",
        sub: []
      })
    }
    setEquipmentTypes(types)
    return types
  }

  const onChangeReportType = (_: string, selectedReport: any) => {
    const { title, value } = selectedReport
    if (reportType?.value === value) return
    const props = {
      reportType: { title, value },
      switch: true
    }
    dispatch(setReportType(props))
    if (value === CHILLER_REPORT || value === AHU_REPORT) {
      setCalendarView({
        start: MONTH,
        end: DATE
      })
      const maxStartDate = moment(new Date()).startOf("month").toDate()
      const safeEndDate = createSafeDate(endDates, new Date())
      const monthEndDate = moment(safeEndDate).endOf("month").toDate()
      const maxEndDate = buildingSummary?.tz
        ? momentTimeZone().tz(buildingSummary?.tz)?.toDate()
        : momentTimeZone().tz("UTC")?.toDate()
      setSelectedDate({
        start: maxStartDate,
        end: moment(monthEndDate)?.isAfter(maxEndDate)
          ? maxEndDate
          : monthEndDate
      })
      dispatch(
        setFilter({
          equipmentType:
            value === CHILLER_REPORT
              ? getPreSelectedEquipType({
                  isBuildingLevel: isBuilding,
                  reportValue: value,
                  equipmentType: equipment?.type,
                  equipmentFamily: equipment?.family,
                  hasHpChillerPermission:
                    accessControlFunc({
                      id: "cc.hp-chiller.features",
                      userAccess
                    }) || false
                })
              : { label: AIR_HANDLER, value: AHU },
          timePeriod: ONE_MONTH,
          startDate: maxStartDate?.toISOString(),
          endDate: moment(monthEndDate)?.isAfter(maxEndDate)
            ? maxEndDate?.toISOString()
            : monthEndDate?.toISOString()
        })
      )
    } else if (value === TRS_REPORT) {
      setCalendarView({
        start: DATE,
        end: DATE
      })
      const maxStartDate = moment(new Date()).startOf("month").toDate()
      const safeEndDate = createSafeDate(endDates, new Date())
      const monthEndDate = moment(safeEndDate).endOf("month").toDate()
      const maxEndDate = buildingSummary?.tz
        ? momentTimeZone().tz(buildingSummary?.tz)?.toDate()
        : momentTimeZone().tz("UTC")?.toDate()
      setSelectedDate({
        start: maxStartDate,
        end: moment(monthEndDate)?.isAfter(maxEndDate)
          ? maxEndDate
          : monthEndDate
      })
      dispatch(
        setFilter({
          equipmentType: getPreSelectedEquipType({
            isBuildingLevel: isBuilding,
            reportValue: value,
            equipmentType: equipment?.type,
            equipmentFamily: equipment?.family,
            hasHpChillerPermission:
              accessControlFunc({
                id: "cc.hp-chiller.features",
                userAccess
              }) || false
          }),
          timePeriod: ONE_MONTH,
          startDate: maxStartDate?.toISOString(),
          endDate: moment(monthEndDate)?.isAfter(maxEndDate)
            ? maxEndDate?.toISOString()
            : monthEndDate?.toISOString()
        })
      )
    } else {
      setCalendarView({
        start: DATE,
        end: DATE
      })
      if (!isBuilding && (value === EXCEPTION_REPORT || value === RAW_DATA)) {
        dispatch(
          setFilter({
            equipmentType: getPreSelectedEquipType({
              isBuildingLevel: isBuilding,
              reportValue: value,
              equipmentType: equipment?.type,
              equipmentFamily: equipment?.family,
              hasHpChillerPermission: false
            })
          })
        )
      }
    }
  }

  const onClickGenerate = useCallback(
    (saveTo: string | undefined | null = null) => {
      if (reportType?.value === RAW_DATA) {
        {
          rawDataCompRef?.current?.onClickGenerate()
          trackEvent(USER_EVENTS.REPORTS.events.CREATE_RAW_DATA_REPORT)
        }
      } else if (reportType?.value === EXCEPTION_REPORT) {
        {
          exceptionCompRef?.current?.onClickGenerate(saveTo)
          trackEvent(USER_EVENTS.REPORTS.events.CREATE_EXCEPTION_HISTORY_REPORT)
        }
      } else if (reportType?.value === CHILLER_REPORT) {
        {
          chillerCompRef?.current?.onClickGenerate(saveTo)
          trackEvent(
            USER_EVENTS.REPORTS.events.CREATE_CHILLER_PERFORMANCE_REPORT
          )
        }
      } else if (reportType?.value === AHU_REPORT) {
        {
          ahuCompRef?.current?.onClickGenerate(saveTo)
          trackEvent(USER_EVENTS.REPORTS.events.CREATE_AHU_PERFORMANCE_REPORT)
        }
      } else if (reportType?.value === DIGITAL_INSPECTION_REPORT_VRF) {
        {
          digitalInspCompRef?.current?.onClickGenerate()
          trackEvent(
            USER_EVENTS.REPORTS.events.CREATE_DIGITAL_INSPECTION_REPORT,
            { type: "DI", objectType: "VRF" }
          )
        }
      } else if (reportType?.value === ALARMS_AND_EVENTS_REPORT) {
        {
          alarmAndEventCompRef?.current?.onClickGenerate(saveTo)
          trackEvent(USER_EVENTS.REPORTS.events.CREATE_ALARMS_REPORT)
        }
      } else if (reportType?.value === TRS_REPORT) {
        trsCompRef?.current?.onClickGenerate(saveTo)
        trackEvent(USER_EVENTS.REPORTS.events.CREATE_TRS_REPORT)
      }
    },
    [
      chillerCompRef,
      ahuCompRef,
      trsCompRef,
      rawDataCompRef,
      digitalInspCompRef,
      showReportDialog,
      reportType
    ]
  )

  const [saveActionState, saveActionDispatch] = useReducer(saveActionReducer, {
    openSaveOptionDialog: false
  })

  useEffect(() => {
    if (saveActionState.saveTo) {
      trackEvent(USER_EVENTS.REPORTS.events.SAVE_REPORT, {
        Origin: "Reports",
        Type: reportType?.value,
        "Saved in Cloud": saveActionState.saveTo === "cloud",
        "Object Type": selectedEquipmentType?.label
      })
      onClickGenerate(saveActionState.saveTo)
    }
  }, [saveActionState.saveTo])

  const closeReportModal = useCallback(() => {
    // Reset the value by using the close action which resets actual state (saveActionState)
    saveActionDispatch({ type: "close" })
    dispatch(setReportDialog(false))
  }, [])

  const modalConfig = useMemo(
    () => ({
      modal: showReportDialog,
      heading: "Reports",
      fullScreen: true,
      buttons: [
        {
          type: "button",
          variant: "blue",
          handleClick: () => {
            const rTypes = [
              EXCEPTION_REPORT,
              CHILLER_REPORT,
              AHU_REPORT,
              TRS_REPORT,
              ALARMS_AND_EVENTS_REPORT
            ]

            // [flagging 2363 feautre]
            if (rTypes.includes(reportType?.value))
              saveActionDispatch({ type: "open" })
            else onClickGenerate()
          },
          text: t("reports.generateReport"),
          disabled: !isValid || loading || isDataExceeded
        },
        {
          type: "button",
          variant: "dark",
          handleClick: closeReportModal,
          text: t("common.close")
        }
      ],
      handleClose: closeReportModal
    }),
    [showReportDialog, loading, isValid, isDataExceeded]
  )

  const errorModalConfig = useMemo(
    () => ({
      modal: error,
      heading: t("reports.errorModal.retryLoadError"),
      isShowCloseIcon: false,
      buttons: [
        {
          type: "button",
          variant: "dark",
          handleClick: () => dispatch(setReportStatus(ACTIONS.RESET)),
          text: t("common.close")
        }
      ]
    }),
    [error]
  )

  const fetchEquipments = async () => {
    setEquipmentTypes([])
    setEquipmentTypesLoading(true)
    let equipmentTypes = []
    try {
      if (reportType?.value === EXCEPTION_REPORT) {
        let buildingIds = []
        if (appliedBuildings?.length >= 1) {
          buildingIds = appliedBuildings?.map((building) => {
            return building?.id
          })
        } else {
          buildingIds = [buildingSummary?.id]
        }
        const finalPayload = { buildingIds: buildingIds, filter: ["analytics"] }
        const apiData: any = await API.graphql({
          query: getEquipmentTypesByBuilding,
          variables: { requestBody: JSON.stringify(finalPayload) }
        })
        const parsedData = JSON.parse(apiData?.data?.getBuildingEquipmentTypes)
          ?.body?.EquipmentType
        if (!appliedBuildings?.length && buildingId) {
          const allEquipment = await fetchBuildingEquipment(buildingId)
          ehrParsedDataRef.current = { parsedData, allEquipment }
          processEHREquipmentTypes(parsedData, allEquipment)
        } else {
          Object.keys(parsedData)?.forEach((item) => {
            if (item !== NONE) {
              equipmentTypes = [
                ...equipmentTypes,
                { title: item, value: parsedData[item], type: "button" }
              ]
            }
          })
          setEquipmentTypes(
            equipmentTypes.length > 0
              ? [...SELECT_ALL, ...equipmentTypes]
              : equipmentTypes
          )
          dispatch(setBuildingEquipmentTypes(equipmentTypes))
        }
      } else if (reportType?.value === RAW_DATA) {
        const data = cloneDeep(await fetchBuildingEquipment(buildingId))
        rawEquipmentDataRef.current = data
        await processEquipmentList(data)
      } else if (reportType?.value === CHILLER_REPORT) {
        const allEquipment = await fetchBuildingEquipment(buildingId)
        chillerEquipmentDataRef.current = allEquipment
        const activeTypes = setChillerReportEquipmentTypes(allEquipment)
        const currentVal = selectedEquipmentType?.value
        if (currentVal) {
          const isValid = activeTypes.some((t) => t.value === currentVal)
          if (isValid) fetchEquipmentsByEquipmentId(currentVal)
          else dispatch(setFilter({ equipmentType: EMPTY_EQUIPMENT_TYPE }))
        }
      } else if (reportType?.value === TRS_REPORT) {
        const allEquipment = await fetchBuildingEquipment(buildingId)
        const activeTypes = setChillerReportEquipmentTypes(allEquipment)
        const currentVal = selectedEquipmentType?.value
        if (currentVal) {
          const isValid = activeTypes.some((t) => t.value === currentVal)
          if (isValid) fetchEquipmentsByEquipmentId(currentVal)
          else dispatch(setFilter({ equipmentType: EMPTY_EQUIPMENT_TYPE }))
        }
      } else if (reportType?.value === ALARMS_AND_EVENTS_REPORT) {
        fetchDevicesByBuildingId({ id: buildingId })
      } else if (reportType?.value === DIGITAL_INSPECTION_REPORT_VRF) {
        const availableEquipmentTypes = ["VRF-System"]
        if (availableEquipmentTypes?.length > 0) {
          const DIEquipmentTypeFilter = availableEquipmentTypes?.map(
            (item) => ({
              type: { eq: item }
            })
          )
          const data = cloneDeep(
            await fetchBuildingEquipment(buildingId, {
              or: DIEquipmentTypeFilter
            })
          )
          await processEquipmentList(data)
          setAvailableEquipmentTypes(false)
        } else {
          setAvailableEquipmentTypes(true)
        }
      } else if (reportType?.value === AHU_REPORT) {
        const allEquipment = await fetchBuildingEquipment(buildingId)
        ahuEquipmentDataRef.current = allEquipment

        const activeTypes = setAHUReportEquipmentTypes(allEquipment)

        const currentVal = selectedEquipmentType?.value
        if (currentVal) {
          const isValid = activeTypes.some((t) => t.value === currentVal)
          if (isValid) fetchEquipmentsByEquipmentId(currentVal)
          else dispatch(setFilter({ equipmentType: EMPTY_EQUIPMENT_TYPE }))
        }
      }
    } finally {
      setEquipmentTypesLoading(false)
    }
  }

  const handleDateRangeSubmit = (selectedDate: any) => {
    if (selectedDate?.start && selectedDate?.end) {
      dispatch(
        setFilter({
          startDate: selectedDate.start.toISOString(),
          endDate: selectedDate.end.toISOString()
        })
      )
    }
  }

  const handleTimePeriodChange = (val: any) => {
    dispatch(setFilter({ timePeriod: val }))
    if (val === ONE_WEEK) {
      const maxStartDate = getCalculatedDay(SUBTRACT, ONE_WEEK)
      if (selectedDate?.start > maxStartDate) {
        setSelectedDate({
          start: maxStartDate,
          end: new Date()
        })
        dispatch(
          setFilter({
            startDate: maxStartDate.toISOString(),
            endDate: new Date().toISOString()
          })
        )
      } else {
        setSelectedDate((prvsVal) => ({
          start: prvsVal?.start,
          end: getCalculatedDay(ADD, ONE_WEEK, prvsVal?.start)
        }))
        if (selectedDate?.start) {
          const newEndDate = getCalculatedDay(ADD, ONE_WEEK, selectedDate.start)
          dispatch(
            setFilter({
              startDate: selectedDate.start.toISOString(),
              endDate: newEndDate.toISOString()
            })
          )
        }
      }
    } else if (val === FOUR_WEEKS) {
      const maxStartDate = getCalculatedDay(SUBTRACT, FOUR_WEEKS)
      setSelectedDate({
        start: maxStartDate,
        end: new Date()
      })
      dispatch(
        setFilter({
          startDate: maxStartDate.toISOString(),
          endDate: new Date().toISOString()
        })
      )
    } else if (val === ONE_MONTH) {
      const maxStartDate = getCalculatedDay(SUBTRACT, ONE_MONTH)
      setSelectedDate({
        start: maxStartDate,
        end: new Date()
      })
      dispatch(
        setFilter({
          startDate: maxStartDate.toISOString(),
          endDate: new Date().toISOString()
        })
      )
    } else if (val === CUSTOM_RANGE) {
      setSelectedDate((prvsVal) => ({
        start: prvsVal?.start,
        end: prvsVal?.start > prvsVal?.end ? prvsVal?.start : prvsVal?.end
      }))
      dispatch(
        setFilter({
          startDate: selectedDate.start.toISOString(),
          endDate:
            selectedDate.start > selectedDate.end
              ? selectedDate.start.toISOString()
              : selectedDate.end.toISOString()
        })
      )
    } else {
      const today = new Date()
      setSelectedDate({
        start: today,
        end: today
      })
      dispatch(
        setFilter({
          startDate: today.toISOString(),
          endDate: today.toISOString()
        })
      )
    }
  }
  const handleLanguageChange = (val: any) => {
    const languageDetails = languageOptions?.find((obj) => obj?.value === val)
    if (languageDetails?.title) {
      setSelectedLanguage({
        title: languageDetails?.title,
        langCode: languageDetails?.value
      })
    } else {
      dispatch(
        setFilter({
          language: LANG_ENGLISH
        })
      )
      const enLanguage = languageOptions?.find(
        (obj) => obj?.value === LANG_ENGLISH
      )
      setSelectedLanguage({
        title: enLanguage?.title,
        langCode: enLanguage?.value
      })
    }
  }

  const handleSystemOfMeasureChange = (val: any) => {
    const metricDetails = systemOfMeasureOptions?.find(
      (obj) => obj?.value === val
    )
    setSelectedMetric({
      title: metricDetails?.title,
      metricCode: metricDetails?.value
    })
  }

  const handleEquipmentType = (_: any, objectValue: any) => {
    dispatch(setReportStatus(ACTIONS.RESET))
    dispatch(setSelectedEquipments([]))
    dispatch(setSelectedProperties([]))
    if (
      reportType?.value === RAW_DATA ||
      reportType?.value === DIGITAL_INSPECTION_REPORT_VRF
    ) {
      dispatch(
        setRawDataEquipments({
          type: "Success",
          data: objectValue?.sub || []
        })
      )
    }
    if (
      reportType?.value === CHILLER_REPORT ||
      reportType?.value === AHU_REPORT ||
      reportType?.value === TRS_REPORT
    ) {
      fetchEquipmentsByEquipmentId(objectValue?.value)
    }
    dispatch(
      setFilter({
        equipmentType: {
          label: objectValue?.title,
          value: objectValue?.value
        },
        error: false,
        isCompleted: false
      })
    )
  }

  const fetchEquipmentsByEquipmentId = async (value: any) => {
    dispatch(setRawDataEquipments({ type: "Loading" }))
    refetchEquipmentsByEquipmentId({
      buildingId: buildingId,
      type: value
    })
  }
  const fetchDevicesByBuildingId = async (value: any) => {
    dispatch(setRawDataEquipments({ type: "Loading" }))
    refetchDevicesByBuildingId(value)
  }
  const timePeriodsOptions: any = useMemo(
    () =>
      reportTimePeriodOptions?.[reportType?.value] || defaultTimePeriodOptions,
    [reportType?.value]
  )

  const facilityNameText = useMemo(() => {
    if (appliedBuildings?.length === 0 || reportType?.value === RAW_DATA) {
      return buildingSummary?.name
    } else if (appliedBuildings?.length === 1) {
      return appliedBuildings[0]?.name
    } else {
      return t("reports.multipleFacilities")
    }
  }, [appliedBuildings, buildingSummary?.name, reportType?.value])

  const lDateFormat = getDateFormatByLocale("MMMM D, YYYY")

  const disableEditBtn = useMemo(
    () =>
      reportType?.value === RAW_DATA ||
      reportType?.value === CHILLER_REPORT ||
      reportType?.value === AHU_REPORT ||
      reportType?.value === TRS_REPORT ||
      reportType?.value === ALARMS_AND_EVENTS_REPORT ||
      reportType?.value === DIGITAL_INSPECTION_REPORT_VRF ||
      buildingListLoading,
    [reportType?.value, buildingListLoading]
  )

  const timePeriodTitleSelected = useMemo(
    () => timePeriodsOptions?.find((obj) => obj?.value === timePeriod)?.title,
    [timePeriod]
  )
  const startDateD = useMemo(
    () => displayDateFormatter(new Date(startDate)),
    [startDate]
  )
  const endDateD = useMemo(
    () => displayDateFormatter(new Date(endDate)),
    [endDate]
  )
  const submittedDateDis =
    selectedLang === "en"
      ? startDateD + (timePeriod !== ONE_DAY ? " - " + endDateD : "")
      : moment(startDateD).format(lDateFormat) +
        (timePeriod !== ONE_DAY
          ? " - " + moment(endDateD).format(lDateFormat)
          : "")

  const getModalComponent = () => {
    switch (reportType?.value) {
      case RAW_DATA:
        return (
          <RawDataReport
            ref={rawDataCompRef}
            selectedEquipmentType={selectedEquipmentType}
            selectedLanguage={selectedLanguage}
            selectedMetric={selectedMetric}
            equipmentProperties={
              chartFilter?.equipmentProperties ||
              buildingLevelEquipmentProperties
            }
          />
        )
      case EXCEPTION_REPORT:
        return (
          <ExceptionReport
            ref={exceptionCompRef}
            selectedLanguage={selectedLanguage}
            selectedMetric={selectedMetric}
          />
        )
      case CHILLER_REPORT:
        return (
          <ChillerReport
            ref={chillerCompRef}
            selectedLanguage={selectedLanguage}
            selectedMetric={selectedMetric}
            selectedEquipmentType={selectedEquipmentType}
          />
        )
      case AHU_REPORT:
        return (
          <AHUReport
            ref={ahuCompRef}
            selectedLanguage={selectedLanguage}
            selectedMetric={selectedMetric}
            selectedEquipmentType={selectedEquipmentType}
          />
        )
      case ALARMS_AND_EVENTS_REPORT:
        return (
          <AlarmsAndEventsReport
            ref={alarmAndEventCompRef}
            selectedLanguage={selectedLanguage}
          />
        )
      case DIGITAL_INSPECTION_REPORT_VRF:
        return (
          <DigitalInspectionReport
            ref={digitalInspCompRef}
            selectedEquipmentType={selectedEquipmentType}
            selectedLanguage={selectedLanguage}
          />
        )
      case TRS_REPORT:
        return (
          <TrsReport
            ref={trsCompRef}
            selectedLanguage={selectedLanguage}
            selectedMetric={selectedMetric}
          />
        )
    }
  }

  const cancelReport = () => {
    dispatch(setReportStatus(ACTIONS.CANCELLED))
    // Reset the value by using the close action which resets actual state (saveActionState)
    saveActionDispatch({ type: "close" })

    trackEvent(USER_EVENTS.REPORTS.events.CANCEL_GENERATE_REPORT)
  }

  const handleCustomDateSelection = (
    calendarType: any,
    date: any,
    pickerCategory: any
  ) => {
    if (
      calendarType === START &&
      moment(selectedDate?.end).isSameOrBefore(moment(date), "day")
    ) {
      const end = new Date(date)
      end.setDate(date.getDate() + 1)
      setSelectedDate({
        start: date,
        end: end
      })
    } else if (
      calendarType === END &&
      moment(selectedDate?.start).isSameOrAfter(moment(date), "day")
    ) {
      const start = new Date(date)
      start.setDate(date.getDate() - 1)
      setSelectedDate({
        start: start,
        end: date
      })
    } else {
      setSelectedDate((prvsVal) => ({
        start:
          calendarType === START
            ? date
            : pickerCategory === ONE_DAY
            ? prvsVal?.start
            : pickerCategory === CUSTOM_RANGE
            ? date < prvsVal?.start
              ? date
              : prvsVal?.start
            : getCalculatedDay(SUBTRACT, pickerCategory, date),
        end:
          calendarType === END
            ? date
            : pickerCategory === ONE_DAY
            ? date
            : pickerCategory === CUSTOM_RANGE
            ? date > prvsVal?.end
              ? date
              : prvsVal?.end
            : getCalculatedDay(ADD, pickerCategory, date)
      }))
    }
  }

  const getMaxStartDateProps = () => {
    if (
      reportType?.value === DIGITAL_INSPECTION_REPORT_VRF &&
      timePeriod === CUSTOM_RANGE
    ) {
      return new Date(buildingSummary?.localTime).setDate(
        new Date(buildingSummary?.localTime).getDate() - 1
      )
    } else {
      return null
    }
  }

  return (
    <>
      <ModalDialog
        modalConfig={modalConfig}
        titleClassName={style.reportDialogHeader}
        bodyClassName={style.reportDialogBody}
        footerClassName={style.reportDialogFooter}
        showSpinner={loading}
        showCompleted={completed}
        showDataExceeded={isDataExceeded}
        completedMessage={completedMessage}
      >
        <SecondaryNav className={style.textAlign}>
          <Typography
            tagType="span"
            styleType="p4"
            data-testid="report-dialog-facility-name-label"
            className={style.fontStyle}
          >
            <>
              {translate("Facility Name")}: {facilityNameText}
            </>
          </Typography>
          <Button
            type="button"
            onClick={() => dispatch(setMultiBuildingsDialog(true))}
            disabled={disableEditBtn}
            testId="report-dialog-edit-button"
          >
            {buildingListLoading ? (
              <div className={style.spinner}>
                <Spinner loading={buildingListLoading} />
              </div>
            ) : (
              <span>
                <TranslateComponent>Edit</TranslateComponent>
              </span>
            )}
          </Button>
          <span className={style.divider} />
          <Typography
            tagType="span"
            styleType="p4"
            data-testid="report-dialog-report-label"
          >
            <TranslateComponent>Report:</TranslateComponent>
          </Typography>
          <MultiLevelDropdown
            translateCurrentViewOff={false}
            title={reportType?.title}
            options={reportTypes}
            handler={onChangeReportType}
            testId="report-dialog-report-types-dropdown-field"
            actionClassName={style.dropdownFontStyle}
          />
          <span className={style.divider} />
          <Typography
            tagType="span"
            styleType="p4"
            data-testid="report-dialog-time-period-label"
            className={style.fontStyle}
          >
            <TranslateComponent>Time Period:</TranslateComponent>
          </Typography>
          <MultiLevelDropdown
            title={timePeriodTitleSelected}
            options={timePeriodsOptions}
            handler={handleTimePeriodChange}
            testId="report-dialog-time-period-dropdown-field"
            actionClassName={style.DropdownFontStyle}
            disabled={
              reportType?.value === CHILLER_REPORT ||
              reportType?.value === AHU_REPORT
            }
          />
          <DateRangePicker
            openCalendar={openCalendar}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            calendarView={calendarView}
            setCalendarView={setCalendarView}
            pickerCategory={timePeriod}
            setOpenCalendar={setOpenCalendar}
            maxStartDateProps={getMaxStartDateProps()}
            maxEndDateProps={new Date(buildingSummary?.localTime)}
            submitHandler={handleDateRangeSubmit}
            testId={"report-dialog--range-date-picker-field"}
            handleCustomDateSelection={
              reportType?.value === DIGITAL_INSPECTION_REPORT_VRF &&
              timePeriod === CUSTOM_RANGE
                ? handleCustomDateSelection
                : null
            }
          />
          <Typography tagType="span" styleType="p4" className={style.fontStyle}>
            {submittedDateDis}
          </Typography>

          {reportType?.value !== ALARMS_AND_EVENTS_REPORT && (
            <>
              <span className={style.divider} />
              <Typography
                tagType="span"
                styleType="p4"
                className={style.fontStyle}
              >
                <TranslateComponent>
                  Select System or Equipment Type:
                </TranslateComponent>
              </Typography>
              <span
                className={equipmentTypesLoading ? style.disabledWrapper : ""}
                aria-busy={equipmentTypesLoading}
              >
                <MultiLevelDropdown
                  translateCurrentViewOff={false}
                  title={selectedEquipmentType?.label || "-- Select One --"}
                  customTranslationOn={true}
                  options={equipmentTypes}
                  handler={handleEquipmentType}
                  testId="report-dialog-equipment-types-dropdown-field"
                  disabled={noAvailableEquipmentTypes || equipmentTypesLoading}
                  enableScroll={reportType?.value === EXCEPTION_REPORT}
                  actionClassName={style.dropdownFontStyle}
                />
              </span>
            </>
          )}

          {(reportType?.value === CHILLER_REPORT ||
            reportType?.value === TRS_REPORT ||
            reportType?.value === RAW_DATA ||
            reportType?.value === DIGITAL_INSPECTION_REPORT_VRF ||
            reportType?.value === ALARMS_AND_EVENTS_REPORT ||
            reportType?.value === AHU_REPORT ||
            reportType?.value === EXCEPTION_REPORT) && (
            <>
              <span className={style.divider} />
              <Typography
                tagType="span"
                styleType="p4"
                className={style.fontStyle}
              >
                <TranslateComponent>Select Language:</TranslateComponent>
              </Typography>
              <MultiLevelDropdown
                translateCurrentViewOff={true}
                title={selectedLanguage?.title}
                options={languageOptions}
                handler={handleLanguageChange}
                testId="report-dialog-language-types-dropdown-field"
                actionClassName={style.dropdownFontStyle}
                // disabled={reportType?.value === DIGITAL_INSPECTION_REPORT}
              />
            </>
          )}

          {(reportType?.value === CHILLER_REPORT ||
            reportType?.value === TRS_REPORT ||
            // reportType?.value === DIGITAL_INSPECTION_REPORT ||
            reportType?.value === RAW_DATA ||
            reportType?.value === AHU_REPORT ||
            reportType?.value === EXCEPTION_REPORT) && (
            <>
              <span className={style.divider} />
              <Typography
                tagType="span"
                styleType="p4"
                className={style.fontStyle}
              >
                <TranslateComponent>
                  Select System Of Measure:
                </TranslateComponent>
              </Typography>
              <MultiLevelDropdown
                title={selectedMetric?.title}
                options={systemOfMeasureOptions}
                handler={handleSystemOfMeasureChange}
                testId="report-dialog-system-of-measure-type-dropdown-field"
                actionClassName={style.dropdownFontStyle}
                // disabled={reportType?.value === DIGITAL_INSPECTION_REPORT}
              />
            </>
          )}
        </SecondaryNav>
        {getModalComponent()}
        {loading && (
          <LoadingSpinner
            progressMessage={
              saveActionState.saveTo === "cloud"
                ? "Saving to the cloud"
                : t("reports.export_started")
            }
            cancelText={t("common.cancel_loading")}
            handleCancel={cancelReport}
            showCancel
            showProgress
          />
        )}
        <SaveDialog
          title={translate(reportType.title)}
          show={saveActionState.openSaveOptionDialog}
          handleAction={saveActionDispatch}
          handleCloseIcon={() => saveActionDispatch({ type: "close" })}
        />
      </ModalDialog>

      {error && (
        <ReportErrorMessage modalConfig={errorModalConfig}></ReportErrorMessage>
      )}
    </>
  )
}

interface SaveAction {
  type: "open" | "close" | "save"
  saveTo?: "local" | "cloud" | "all"
}

interface SaveActionState {
  openSaveOptionDialog: boolean
  saveTo?: "local" | "cloud" | "all"
}

function saveActionReducer(state: SaveActionState, action: SaveAction) {
  const { type, saveTo } = action ?? {}

  switch (type) {
    case "open": {
      return {
        openSaveOptionDialog: true
      }
    }
    case "close": {
      return {
        openSaveOptionDialog: false
      }
    }
    case "save": {
      return {
        saveTo,
        openSaveOptionDialog: false
      }
    }
  }
  throw Error("Unknown action: " + type)
}

export default ReportDialog

